import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../components/ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { 
  Plus, Search, Pencil, Trash2, User, Phone, MapPin, Stethoscope,
  FileText, X, Check, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhysiciansPage = () => {
  const { currentOrg } = useAuth();
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingPhysician, setEditingPhysician] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    specialty: '',
    phone: '',
    address: '',
    notes: ''
  });

  const fetchPhysicians = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: currentOrg.id });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/physicians?${params}`);
      setPhysicians(res.data || []);
    } catch (error) {
      console.error('Failed to fetch physicians:', error);
      toast.error('Failed to load physicians');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhysicians();
  }, [currentOrg?.id, search]);

  const resetForm = () => {
    setFormData({ name: '', name_ar: '', specialty: '', phone: '', address: '', notes: '' });
    setEditingPhysician(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (physician) => {
    setEditingPhysician(physician);
    setFormData({
      name: physician.name || '',
      name_ar: physician.name_ar || '',
      specialty: physician.specialty || '',
      phone: physician.phone || '',
      address: physician.address || '',
      notes: physician.notes || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Physician name is required');
      return;
    }

    try {
      if (editingPhysician) {
        await axios.put(`${API}/physicians/${editingPhysician.id}`, formData);
        toast.success('Physician updated');
      } else {
        await axios.post(`${API}/physicians`, {
          ...formData,
          organization_id: currentOrg.id
        });
        toast.success('Physician added');
      }
      setShowDialog(false);
      resetForm();
      fetchPhysicians();
    } catch (error) {
      console.error('Failed to save physician:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (physician) => {
    if (!window.confirm(`Delete ${physician.name}?`)) return;
    try {
      await axios.delete(`${API}/physicians/${physician.id}`);
      toast.success('Physician deleted');
      fetchPhysicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Stethoscope className="w-6 h-6 text-primary" />
            Physicians
          </h1>
          <p className="text-muted-foreground text-sm">Manage prescribing physicians/doctors</p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-physician-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Physician
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, specialty, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="physician-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-2xl font-bold">{physicians.length}</div>
            <div className="text-xs text-muted-foreground">Total Physicians</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Check className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <div className="text-2xl font-bold">{physicians.filter(p => p.is_active !== false).length}</div>
            <div className="text-xs text-muted-foreground">Active</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 mx-auto mb-2 text-blue-500" />
            <div className="text-2xl font-bold">{physicians.reduce((sum, p) => sum + (p.prescription_count || 0), 0)}</div>
            <div className="text-xs text-muted-foreground">Total Prescriptions</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Arabic Name</TableHead>
                <TableHead>Specialty</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-center">Prescriptions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : physicians.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No physicians found
                  </TableCell>
                </TableRow>
              ) : (
                physicians.map(physician => (
                  <TableRow key={physician.id}>
                    <TableCell className="font-medium">{physician.name}</TableCell>
                    <TableCell className="text-muted-foreground" dir="rtl">{physician.name_ar || '-'}</TableCell>
                    <TableCell>{physician.specialty || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{physician.phone || '-'}</TableCell>
                    <TableCell className="text-center">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                        {physician.prescription_count || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${physician.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {physician.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(physician)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(physician)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              {editingPhysician ? 'Edit Physician' : 'Add Physician'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Dr. John Smith"
                  data-testid="physician-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Arabic Name</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  placeholder="د. جون سميث"
                  dir="rtl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Specialty</Label>
              <Input
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                placeholder="e.g., General Practitioner, Cardiologist"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+961 XX XXX XXX"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Clinic address"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-physician-btn">
              <Check className="w-4 h-4 mr-2" />
              {editingPhysician ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhysiciansPage;
